import { StatCard } from "@/components/dashboard/StatCard";
import { VolumeChart } from "@/components/dashboard/VolumeChart";
import { RecentFillsTable } from "@/components/dashboard/RecentFillsTable";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useWallet } from "@/hooks/useWallet";
import { Activity, DollarSign } from "lucide-react";

export default function PerpsTrades() {
  const { address } = useWallet();
  const {
    data,
    isLoading,
    error,
    exportCsv,
    rangeDays,
    setRangeDays,
  } = useDashboardData({ address, rangeDays: 30, marketType: 'perps' });

  const formatValue = (value: number | null | undefined, prefix = "", suffix = "") => {
    if (value == null) return "N/A";
    if (prefix === "$") {
      if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      return `$${value.toFixed(2)}`;
    }
    return `${prefix}${value.toLocaleString()}${suffix}`;
  };

  return (
    <main className="container px-4 py-8 space-y-6">
      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Trades</h2>
        <div className="flex items-center gap-2">
          <button
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => exportCsv('fills')}
          >
            Export fills
          </button>
          <button
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => exportCsv('daily')}
          >
            Export daily
          </button>
          <select
            className="border border-border bg-background rounded-md px-2 py-1 text-sm"
            value={rangeDays}
            onChange={(e) => setRangeDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>All time</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Fills"
          value={formatValue(data?.kpis.fillsCount)}
          icon={<Activity className="w-5 h-5 text-primary" />}
          loading={isLoading}
          tooltip="Number of perps trade fills executed in the selected date range."
        />
        <StatCard
          title="Total Volume"
          value={formatValue(data?.kpis.totalVolume, "$")}
          icon={<DollarSign className="w-5 h-5 text-primary" />}
          loading={isLoading}
          tooltip="Total perps notional traded (sum of fill notional) in the selected date range."
        />
        <StatCard
          title="Total Fees"
          value={formatValue(data?.kpis.totalFees, "$")}
          icon={<DollarSign className="w-5 h-5 text-primary" />}
          loading={isLoading}
          tooltip="Total fees paid on perps fills in the selected date range."
        />
      </div>

      <VolumeChart data={data?.charts.volumeDaily} loading={isLoading} />

      <RecentFillsTable 
        fills={data?.recentFills} 
        loading={isLoading}
        exportContext="perps"
        rangeDays={rangeDays}
      />
    </main>
  );
}
